﻿import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { subscriptionService, type SubscriptionCurrency } from '@/services/subscription.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues { code: string; symbol: string; name: string; is_default: boolean }

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  currency?: SubscriptionCurrency;
  onClose: () => void;
  onSuccess: () => void;
}

export function CurrencyFormDialog({ open, mode, currency, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { code: '', symbol: '', name: '', is_default: false },
  });

  const isDefault = watch('is_default');

  useEffect(() => {
    if (open) {
      reset({
        code: currency?.code ?? '',
        symbol: currency?.symbol ?? '',
        name: currency?.name ?? '',
        is_default: currency?.is_default ?? false,
      });
    }
  }, [open, currency, reset]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      const input = { code: v.code.trim().toUpperCase(), symbol: v.symbol.trim(), name: v.name.trim(), is_default: v.is_default };
      return isEdit && currency
        ? subscriptionService.updateCurrency(currency.id, input)
        : subscriptionService.createCurrency(input);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Currency updated' : 'Currency created');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'currencies'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Currency' : 'New Currency'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cur-code">Code *</Label>
              <Input id="cur-code" {...register('code', { required: 'Code is required' })} placeholder="USD" className="uppercase" />
              {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cur-symbol">Symbol *</Label>
              <Input id="cur-symbol" {...register('symbol', { required: 'Symbol is required' })} placeholder="$" />
              {errors.symbol && <p className="text-xs text-destructive">{errors.symbol.message}</p>}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cur-name">Name *</Label>
            <Input id="cur-name" {...register('name', { required: 'Name is required' })} placeholder="US Dollar" />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="cur-default"
              checked={isDefault}
              onChange={(e) => setValue('is_default', e.target.checked)}
            />
            <Label htmlFor="cur-default" className="cursor-pointer font-normal">
              Set as default currency
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
